import { useState } from 'react'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { CircleAlert, Download, Info } from 'lucide-react'
import { getV1MeRetrieveQueryOptions, v1ReportingStudentDataExportRetrieve } from '@/api/client'
import { BudgetEntriesReport } from '@/components/reporting/budget-entries-report'
import { PollResponsesReport } from '@/components/reporting/poll-responses-report'
import { PageHeader } from '@/components/page-header'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { filenameFromDisposition, saveBlob } from '@/lib/download-file'

export const Route = createFileRoute('/_panel/eksport-danych')({
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(getV1MeRetrieveQueryOptions())
    if (me.status === 200 && me.data.role !== 'superadmin') {
      throw redirect({ to: '/' })
    }
  },
  component: StudentDataExportPage,
})

type ReportTab = 'wpisy-budzetowe' | 'ankiety'

export function StudentDataExportPage() {
  const [downloading, setDownloading] = useState(false)
  const [failed, setFailed] = useState(false)
  const [tab, setTab] = useState<ReportTab>('wpisy-budzetowe')

  const download = async () => {
    setFailed(false)
    setDownloading(true)
    try {
      const response = await v1ReportingStudentDataExportRetrieve()
      if (response.status !== 200) {
        setFailed(true)
        return
      }
      const disposition = response.headers.get('content-disposition')
      saveBlob(response.data, filenameFromDisposition(disposition, 'dane-uczniow.xlsx'))
    } catch {
      setFailed(true)
    } finally {
      setDownloading(false)
    }
  }

  return (
    <>
      <PageHeader
        title="Raporty danych uczniów"
        actions={
          <Button onClick={download} disabled={downloading}>
            {downloading ? (
              <Spinner data-icon="inline-start" />
            ) : (
              <Download data-icon="inline-start" />
            )}
            {downloading ? 'Przygotowuję plik…' : 'Pobierz pełny XLSX'}
          </Button>
        }
      />
      <div className="flex min-w-0 flex-col gap-5 p-4 sm:p-6">
        {failed ? (
          <Alert variant="destructive" className="max-w-2xl">
            <CircleAlert />
            <AlertTitle>Nie udało się pobrać pliku</AlertTitle>
            <AlertDescription>Spróbuj ponownie za chwilę.</AlertDescription>
          </Alert>
        ) : null}
        <Alert role="note" className="max-w-3xl">
          <Info />
          <AlertTitle>Pełny eksport pozostaje niezależny</AlertTitle>
          <AlertDescription>
            Pobierany plik XLSX obejmuje wszystkie rodzaje danych i nie uwzględnia filtrów tabeli.
          </AlertDescription>
        </Alert>

        <Tabs value={tab} onValueChange={(value) => setTab(value as ReportTab)}>
          <TabsList>
            <TabsTrigger value="wpisy-budzetowe">Wpisy budżetowe</TabsTrigger>
            <TabsTrigger value="ankiety">Ankiety</TabsTrigger>
          </TabsList>
          <TabsContent value="wpisy-budzetowe">
            <BudgetEntriesReport />
          </TabsContent>
          <TabsContent value="ankiety">
            <PollResponsesReport />
          </TabsContent>
        </Tabs>
      </div>
    </>
  )
}
